import {
  CREATE_FORM,
  DELETE_FORM,
  FORM_AUDIENCE,
  FORM_AUDIENCE_ADD_NEW_LIST,
  FORM_AUDIENCE_FILTER,
  FORM_DETAILS,
  FORM_SEARCH_AUDIENCE,
  FORMS_LISTING,
  UPDATE_FORM,
} from 'constants/types/formTypes';
import { IActionData, IFieldData, IFormAction, IFormAudienceState, IFormData, IFormState } from './interfaces';
import { internalFieldMappings } from './constants';

const formsInitialState: IFormState = {
  loading: true,
  success: false,
  searchTerm: '',
  forms: {
    loading: false,
    data: [],
    success: false,
    message: '',
    totalCount: -1,
  },
  selectedForm: {
    loading: false,
    data: {
      id: 0,
      name: '',
      campaignId: 0,
      campaignName: '',
      actions: [],
      fields: [],
      createdAt: '',
      lastUpdated: '',
      createdBy: '',
      updatedBy: '',
      formUrl: '',
      submissions: 0,
    },
    success: false,
    message: '',
  },
  createForm: {
    loading: false,
    success: false,
    error: false,
  },
  updateForm: {
    loading: false,
    success: false,
    error: false,
  },
  deleteForm: {
    loading: false,
    success: false,
    error: false,
  },
  cloneForm: {
    loading: false,
    success: false,
    error: false,
  },
};

const formsAudienceInitialState: IFormAudienceState = {
  loading: false,
  contacts: [],
  totalContacts: 0,
  page: 0,
  searchedLoading: false,
  searchedContacts: [],
  searchedPage: 0,
  totalSearchedContacts: 0,
  searchTerm: '',
  message: '',
  success: false,
  zeroContactMapping: false,
  listAlreadyExists: false,
  filter: {
    selectedCategory: {
      value: 'all',
      label: 'All time',
    },
    startDate: '2018-06-01',
    endDate: new Date().toISOString().split('T')[0],
  },
  addNewList: {
    loading: false,
    success: false,
    message: '',
  },
};

export const formsReducer = (state: IFormState = formsInitialState, action: IFormAction) => {
  const newState = { ...state };
  switch (action.type) {
    // Form Deatils
    case FORM_DETAILS.LOAD:
      newState.selectedForm.loading = true;
      return newState;

    case FORM_DETAILS.SUCCESS:
      const { id, name, campaign_id, campaign_name, created_at, updated_at, created_by, updated_by, form_url, actions, fields, submissions } =
        action.payload.data;
      newState.selectedForm.loading = false;
      // Actions mapped as per the frontend requirement
      const formActions = JSON.parse(actions);
      const updatedFormActions = [...formActions];
      formActions.forEach((action: IActionData, index: number) => {
        if ('atl' in action) {
          const tempAction = formActions[index];
          const key = Object.keys(tempAction)[0];
          const newObj = { ['Add to list']: tempAction[key] };
          delete tempAction[key];
          updatedFormActions[index] = {
            ...newObj,
            ...tempAction,
          };
        }
      });
      // Internal Field mapping
      const updatedFields: IFieldData = JSON.parse(fields);
      const tempFormFields: IFieldData = Object.values(updatedFields).map((field: any) => {
        const keys = Object.keys(internalFieldMappings);
        const values = Object.values(internalFieldMappings);
        let ind: number = -1;
        [...Array(keys.length)].map((item, index) => {
          if (field.internal_field === values[index]) ind = index;
        });
        return {
          ...field,
          internal_field: keys[ind],
        };
      });
      newState.selectedForm.data = {
        id,
        name,
        submissions,
        campaignName: campaign_name,
        campaignId: campaign_id,
        createdAt: created_at,
        lastUpdated: updated_at,
        createdBy: created_by,
        updatedBy: updated_by,
        formUrl: form_url,
        actions: updatedFormActions,
        fields: tempFormFields,
      };
      newState.selectedForm.success = true;
      return newState;

    case FORM_DETAILS.FAIL:
      newState.selectedForm.loading = false;
      newState.selectedForm.success = false;
      return newState;

    // Create Form
    case CREATE_FORM.LOAD:
      newState.createForm.loading = true;
      return newState;

    case CREATE_FORM.SUCCESS:
      newState.forms.totalCount = newState.forms.totalCount === -1 ? 1 : newState.forms.totalCount + 1;
      newState.createForm.loading = false;
      return newState;

    case CREATE_FORM.FAIL:
      newState.createForm.loading = false;
      return newState;

    // Update Form
    case UPDATE_FORM.LOAD:
      newState.updateForm.loading = true;
      return newState;

    case UPDATE_FORM.SUCCESS:
      newState.updateForm.loading = false;
      return newState;

    case UPDATE_FORM.FAIL:
      newState.updateForm.loading = false;
      return newState;

    // Delete Form
    case DELETE_FORM.LOAD:
      newState.deleteForm.loading = true;
      return newState;

    case DELETE_FORM.SUCCESS:
      const { formIds, selectAll, excludedFormIds } = action.payload;
      const forms = [...newState.forms.data];
      let remainingFormIds: IFormData[] = [];
      if (selectAll) {
        remainingFormIds = forms.filter((form: IFormData) => (excludedFormIds as number[]).includes(form.id));
        newState.forms.totalCount = (excludedFormIds as number[]).length;
      } else {
        remainingFormIds = forms.filter((form: IFormData) => !(formIds as number[]).includes(form.id));
        newState.forms.totalCount -= (formIds as number[]).length;
      }
      newState.forms.data = remainingFormIds;
      newState.deleteForm.loading = false;
      return newState;

    case DELETE_FORM.FAIL:
      newState.deleteForm.loading = false;
      return newState;

    // Forms Listing
    case FORMS_LISTING.LOAD:
      newState.forms.loading = true;
      return newState;

    case FORMS_LISTING.SUCCESS:
      newState.forms.loading = false;
      newState.searchTerm = action.payload.searchTerm ?? '';
      newState.loading = false;
      newState.forms.message = action.payload.message;
      if (newState.forms.data.length > 0) {
        newState.forms.data = [...newState.forms.data, ...action.payload.forms];
      } else {
        newState.forms.data = action.payload.forms;
      }
      newState.forms.totalCount = action.payload.totalForms;
      return newState;

    case FORMS_LISTING.FAIL:
      newState.forms.loading = false;
      return newState;

    case FORMS_LISTING.RESET:
      newState.forms.loading = false;
      newState.forms.data = [];
      newState.forms.totalCount = 0;
      return newState;

    default:
      return newState;
  }
};

export const formsAudienceReducer = (state: IFormAudienceState = formsAudienceInitialState, action: IFormAction) => {
  const newState = { ...state };
  switch (action.type) {
    // Form Audience -> Data Load
    case FORM_AUDIENCE.LOAD:
      newState.loading = true;
      return newState;

    case FORM_AUDIENCE.SUCCESS:
      newState.loading = false;
      newState.totalContacts = action.payload.data.total_count;
      if (newState.page === 0) newState.zeroContactMapping = action.payload.data.zero_contact_mapping;
      newState.message = action.payload.data.message;
      newState.page += 1;
      const fetchedContacts = action.payload.data.data;
      fetchedContacts.forEach((contact: any) => {
        const { contact_id, first_name, last_name, full_name, created_on, person_id, origin, origin_domain, ...remaining } = contact;
        const newContact = {
          contactId: contact_id,
          firstName: first_name,
          lastName: last_name,
          fullName: full_name,
          submittedAt: created_on,
          personId: person_id,
          origin: origin,
          originDomain: origin_domain,
          ...remaining,
        };
        newState.contacts.push(newContact);
      });
      newState.success = true;
      return newState;

    case FORM_AUDIENCE.RESET_LIST_ONLY:
      newState.loading = false;
      newState.message = '';
      newState.page = 0;
      newState.contacts = [];
      newState.success = false;

      newState.searchedLoading = false;
      newState.searchedContacts = [];
      newState.searchedPage = 0;
      newState.success = false;
      return newState;

    case FORM_AUDIENCE.RESET:
      newState.loading = false;
      newState.totalContacts = 0;
      newState.message = '';
      newState.page = 0;
      newState.contacts = [];
      newState.success = false;
      return newState;

    case FORM_AUDIENCE.FAIL:
      newState.loading = false;
      newState.success = false;
      return newState;

    case FORM_SEARCH_AUDIENCE.LOAD:
      newState.searchedLoading = true;
      return newState;

    case FORM_SEARCH_AUDIENCE.SUCCESS:
      newState.searchedLoading = false;
      newState.totalSearchedContacts = action.payload.data.total_count;
      if (newState.searchedPage === 0) newState.zeroContactMapping = action.payload.data.zero_contact_mapping;
      newState.message = action.payload.data.message;
      newState.searchedPage += 1;
      const fetchedSearchedContacts = action.payload.data.data;
      fetchedSearchedContacts.forEach((contact: any) => {
        const { contact_id, first_name, last_name, full_name, created_on, person_id, origin, origin_domain, ...remaining } = contact;
        const newSearchedContact = {
          contactId: contact_id,
          firstName: first_name,
          lastName: last_name,
          fullName: full_name,
          submittedAt: created_on,
          personId: person_id,
          origin: origin,
          originDomain: origin_domain,
          ...remaining,
        };
        newState.searchedContacts.push(newSearchedContact);
      });
      newState.success = true;
      return newState;

    case FORM_SEARCH_AUDIENCE.RESET:
      newState.searchedLoading = false;
      newState.totalSearchedContacts = 0;
      newState.message = '';
      newState.searchedContacts = [];
      newState.searchedPage = 0;
      newState.success = false;
      return newState;

    case FORM_SEARCH_AUDIENCE.FAIL:
      newState.searchedLoading = false;
      newState.success = false;
      return newState;

    case FORM_SEARCH_AUDIENCE.SET_SEARCH_TERM:
      newState.searchTerm = action.payload.searchTerm;
      return newState;

    case FORM_SEARCH_AUDIENCE.RESET_SEARCH_TERM:
      newState.searchTerm = '';
      return newState;

    // Forms Audience -> Add New List
    case FORM_AUDIENCE_ADD_NEW_LIST.LOAD:
      newState.addNewList.loading = true;
      return newState;

    case FORM_AUDIENCE_ADD_NEW_LIST.SUCCESS:
      newState.addNewList.loading = false;
      newState.addNewList.success = true;
      newState.addNewList.message = action.payload.message;
      return newState;

    case FORM_AUDIENCE_ADD_NEW_LIST.FAIL:
      newState.addNewList.loading = false;
      newState.addNewList.success = false;
      newState.addNewList.message = action.payload.message;
      return newState;

    // Forms Audience -> Filter
    case FORM_AUDIENCE_FILTER.SET_DATE_SELECTION:
      newState.filter.selectedCategory = action.payload;
      return newState;

    case FORM_AUDIENCE_FILTER.SET_DATE:
      newState.filter.startDate = action.payload.start_date;
      newState.filter.endDate = action.payload.end_date;
      return newState;

    case FORM_AUDIENCE_FILTER.RESET:
      newState.filter.startDate = '2018-06-01';
      newState.filter.endDate = new Date().toISOString().split('T')[0];
      newState.filter.selectedCategory = {
        value: 'all',
        label: 'All time',
      };
      return newState;

    default:
      return newState;
  }
};
